import {
	isClerkAPIResponseError,
	isClerkRuntimeError,
	isReverificationCancelledError,
} from "@clerk/shared/error";

const CANCELLED_CODES = new Set([
	"passkey_registration_cancelled",
	"passkey_retrieval_cancelled",
	"passkey_operation_aborted",
	"reverification_cancelled",
]);

const CANCELLED_NAME = new Set([
	"AbortError",
	"NotAllowedError",
	"TimeoutError",
]);

function collectCodes(err: unknown): string[] {
	const codes: string[] = [];
	if (isClerkRuntimeError(err) && typeof err.code === "string") {
		codes.push(err.code);
	}
	if (isClerkAPIResponseError(err)) {
		for (const item of err.errors) {
			if (item.code) codes.push(item.code);
		}
	}
	if (err && typeof err === "object" && "code" in err) {
		const code = (err as { code?: unknown }).code;
		if (typeof code === "string") codes.push(code);
	}
	if (err && typeof err === "object" && "errors" in err) {
		const errors = (err as { errors?: Array<{ code?: string }> }).errors;
		for (const item of errors ?? []) {
			if (item.code) codes.push(item.code);
		}
	}
	return codes;
}

function collectMessages(err: unknown): string[] {
	const messages: string[] = [];
	if (err instanceof Error && err.message) messages.push(err.message);
	if (isClerkAPIResponseError(err)) {
		for (const item of err.errors) {
			if (item.longMessage) messages.push(item.longMessage);
			if (item.message) messages.push(item.message);
		}
	}
	if (err && typeof err === "object" && "errors" in err) {
		const errors = (
			err as {
				errors?: Array<{ longMessage?: string; message?: string }>;
			}
		).errors;
		for (const item of errors ?? []) {
			if (item.longMessage) messages.push(item.longMessage);
			if (item.message) messages.push(item.message);
		}
	}
	return messages;
}

/**
 * True when the user dismissed a browser/Clerk prompt (passkey, reverification, etc.).
 * These must never surface as red error banners.
 */
export function isUserCancelledError(err: unknown): boolean {
	if (isReverificationCancelledError(err)) return true;

	if (err && typeof err === "object" && "name" in err) {
		const name = (err as { name?: unknown }).name;
		if (typeof name === "string" && CANCELLED_NAME.has(name)) return true;
	}

	const codes = collectCodes(err);
	if (codes.some((code) => CANCELLED_CODES.has(code))) return true;

	const messages = collectMessages(err).map((m) => m.toLowerCase());
	return messages.some(
		(m) =>
			m.includes("cancelled") ||
			m.includes("canceled") ||
			m.includes("aborted") ||
			m.includes("the operation either timed out or was not allowed"),
	);
}

export function isRateLimitError(err: unknown): boolean {
	const codes = collectCodes(err).map((c) => c.toLowerCase());
	if (
		codes.some(
			(c) =>
				c.includes("rate_limit") ||
				c.includes("too_many") ||
				c === "request_too_large",
		)
	) {
		return true;
	}
	if (err && typeof err === "object" && "status" in err) {
		if ((err as { status?: unknown }).status === 429) return true;
	}
	const messages = collectMessages(err).map((m) => m.toLowerCase());
	return messages.some(
		(m) => m.includes("too many requests") || m.includes("rate limit"),
	);
}

/** Human-readable Clerk/API error, or `null` if the user cancelled. */
export function clerkErrorMessage(err: unknown): string | null {
	if (isUserCancelledError(err)) return null;
	if (isRateLimitError(err)) {
		return "Too many attempts — wait about 15 seconds, then try again. Refreshing makes this worse.";
	}

	const messages = collectMessages(err);
	if (messages[0]) return messages[0];
	return "Something went wrong";
}
